import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';

// Luxury brand names across multiple industries
const luxuryBrands = [
  // Fashion & Accessories
  'CHANEL', 'DIOR', 'GUCCI', 'PRADA', 'HERMÈS',
  'LOUIS VUITTON', 'VERSACE', 'BALENCIAGA', 'FENDI', 'GIVENCHY',
  'VALENTINO', 'BURBERRY', 'SAINT LAURENT', 'CELINE', 'BOTTEGA VENETA',
  'ARMANI', 'DOLCE & GABBANA', 'TOM FORD', 'JIMMY CHOO', 'MONCLER',
  'OFF-WHITE', 'ALEXANDER MCQUEEN', 'LOEWE', 'JACQUEMUS', 'LORO PIANA',
  'THE ROW', 'TOTEME', 'LEMAIRE', 'KHAITE', 'ALAÏA',

  // Jewelry & Watches
  'CARTIER', 'TIFFANY', 'ROLEX', 'VAN CLEEF', 'BVLGARI',
  'BRUNELLO CUCINELLI', 'OMEGA', 'PATEK PHILIPPE', 'AUDEMARS PIGUET', 'CHOPARD',
  'GRAFF', 'HARRY WINSTON', 'PIAGET',

  // Automotive
  'ROLLS-ROYCE', 'BENTLEY', 'FERRARI', 'LAMBORGHINI', 'ASTON MARTIN',
  'MCLAREN', 'BUGATTI', 'PORSCHE', 'MAYBACH', 'PAGANI',

  // Hospitality
  'FOUR SEASONS', 'RITZ-CARLTON', 'AMAN', 'ROSEWOOD', 'PENINSULA',
  'MANDARIN ORIENTAL', 'BULGARI HOTELS', 'PARK HYATT', 'ST. REGIS',

  // Aviation
  'GULFSTREAM', 'BOMBARDIER', 'EMBRAER', 'CESSNA CITATION', 'DASSAULT FALCON',
  'NETJETS', 'VISTAJET',

  // Yachts
  'BENETTI', 'AZIMUT', 'FEADSHIP', 'LURSSEN', 'OCEANCO',
  'SUNSEEKER', 'PRINCESS YACHTS',

  // Beauty & Cosmetics
  'LA MER', 'TOM FORD BEAUTY', 'CHANEL BEAUTY', 'DIOR BEAUTY', 'LA PRAIRIE',
  'SISLEY', 'SK-II', 'CLIVE CHRISTIAN',

  // Spirits & Wine
  'DOM PÉRIGNON', 'CRISTAL', 'HENNESSY', 'RÉMY MARTIN', 'MACALLAN',
  'CHÂTEAU LAFITE', 'LOUIS XIII', 'MOËT & CHANDON',

  // Real Estate
  'SOTHEBY\'S REALTY', 'CHRISTIE\'S REAL ESTATE', 'KNIGHT FRANK', 'SAVILLS',

  // Technology
  'BANG & OLUFSEN', 'VERTU', 'GOLDVISH', 'APPLE PRO', 'LOEWE TECH',

  // Art & Auction Houses
  'SOTHEBY\'S', 'CHRISTIE\'S', 'PHILLIPS', 'BONHAMS', 'ARTCURIAL',

  // Lifestyle & Design
  'BACCARAT', 'LALIQUE', 'BERNARDAUD', 'CHRISTOFLE', 'HERMÈS MAISON',
  'LORO PIANA INTERIORS', 'FENDI CASA', 'VERSACE HOME'
];

// Single brand text flowing towards camera in straight line (tunnel effect)
function BrandText({ brand, x, y, initialZ, delay }) {
  const textRef = useRef();

  useFrame((state) => {
    if (textRef.current) {
      const time = state.clock.getElapsedTime() * 2 + delay;

      // Z position: flow from back (-30) to front (5), then reset
      let z = initialZ + time;
      if (z > 5) {
        z = -30 + ((z - 5) % 35);
      }

      textRef.current.position.x = x;
      textRef.current.position.y = y;
      textRef.current.position.z = z;

      // Scale grows as it comes closer (starts small, grows big)
      const progress = (z + 30) / 35; // 0 to 1 as it comes forward
      const scale = 0.05 + progress * 0.15;
      textRef.current.scale.set(scale, scale, scale);

      // Opacity: fade in from distance, fade out when very close
      if (textRef.current.material) {
        let opacity = 0.4;
        if (z < -25) {
          opacity = (z + 30) / 5 * 0.4; // fade in
        } else if (z > 3) {
          opacity = (5 - z) / 2 * 0.4; // fade out
        }
        textRef.current.material.opacity = opacity;
      }
    }
  });

  return (
    <Text
      ref={textRef}
      fontSize={1}
      color="#ffc1cc"
      anchorX="center"
      anchorY="middle"
      transparent
      opacity={0.4}
    >
      {brand}
    </Text>
  );
}

// Tunnel with brand names flowing straight forward
function WormholeBrands() {
  const brandInstances = useMemo(() => {
    const instances = [];
    for (let i = 0; i < 120; i++) {
      instances.push({
        brand: luxuryBrands[i % luxuryBrands.length],
        x: (Math.random() - 0.5) * 10,
        y: (Math.random() - 0.5) * 8,
        initialZ: -30 + (i / 120) * 35,
        delay: i * 0.15
      });
    }
    return instances;
  }, []);

  return (
    <>
      {brandInstances.map((props, i) => (
        <BrandText key={i} {...props} />
      ))}
    </>
  );
}

const ThreeBackground = () => {
  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: 'calc(100% + 400px)',
      zIndex: 0,
      pointerEvents: 'none',
      maskImage: 'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 70%, rgba(0,0,0,0.5) 85%, rgba(0,0,0,0) 100%)',
      WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 70%, rgba(0,0,0,0.5) 85%, rgba(0,0,0,0) 100%)'
    }}>
      <Canvas
        camera={{ position: [0, 0, 5], fov: 75 }}
        style={{ background: 'transparent' }}
      >
        <WormholeBrands />
      </Canvas>
    </div>
  );
};

export default ThreeBackground;
